import { AbstractControl, UntypedFormArray, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { DateValidatorDirective } from '@app/validators/date-validator-directive';
import { FormControlToEnumTypeMapping } from '@app/class/income-before-offer.config';

export class FormUtils {
    static maskPercent(value: any) {
        value = value || 0;
        return Math.floor(value * 100000) / 1000;
    }

    static unmaskPercent(value: any) {
        value = value || 0;
        return parseFloat(value.toString().replace(/\s/g, '').replace(',', '.')) / 100;
    }

    static unmaskCurrency(value: any) {
        value = value || 0;
        return parseInt(value.toString().replace(/\s/g, ''), null);
    }

    public static unmaskCalculatorForm(data) {
        data.amount = this.unmaskCurrency(data.amount);
        data.value = this.unmaskCurrency(data.value);
        data.repayment = this.unmaskCurrency(data.repayment);
        return data;
    }

    static plusValue(control, step) {
        const value = this.unmaskCurrency(control.value);
        const count = Math.floor(value / step);
        control.setValue((count + 1) * step);
        FormUtils.checkValueBoundaries(control);
    }

    static minusValue(control, step) {
        const value = this.unmaskCurrency(control.value);
        let count = Math.ceil(value / step);
        if (count < 1) {
            count = 1;
        }
        const result = (count - 1) * step;
        control.setValue(result);
        FormUtils.checkValueBoundaries(control);
    }

    static checkValueBoundaries(control: AbstractControl) {
        if (control.errors?.min) {
            control.setValue(control.errors?.min?.min);
        } else if (control.errors?.max) {
            control.setValue(control.errors?.max?.max);
        }
    }

    static getValidators(config) {
        const validators = [];
        if (config?.required?.value === true) {
            validators.push(Validators.required);
        }
        if (config?.min?.value) {
            validators.push(Validators.min(config.min.value));
        }
        if (config?.max?.value) {
            validators.push(Validators.max(config.max.value));
        }
        if (config?.date?.value) {
            validators.push(DateValidatorDirective.validateDate);
        }
        return validators;
    }

    static createFormInput(value, config) {
        return [value ?? config.default, FormUtils.getValidators(config.validation)];
    }

    static getErrorTypes(form, itemName: string) {
        if (form?.get(itemName)?.errors) {
            return Object.keys(form?.get(itemName)?.errors);
        } else {
            return [];
        }
    }

    static getErrorMsg(formConfig, itemName: string, errorCode: string) {
        return formConfig?.items[itemName]?.validation[errorCode]?.errorMsg;
    }

    static getSuffix(type) {
        switch (type) {
            case 'money':
            case 'repayment':
                return 'Kč';
            case 'years':
                return 'let';
            case 'percent':
                return '%';
        }
    }

    static conditionalValidator(predicate, validator) {
        return (formControl) => {
            if (!formControl.parent) {
                return null;
            }
            if (predicate()) {
                return validator(formControl);
            }
            return null;
        };
    }

    static shouldShowInput<T>(
        config: FormControlToEnumTypeMapping<T>,
        inputName: string,
        type: T,
        additionalCondition: (inputName: string, type: T, config: FormControlToEnumTypeMapping<T>) => boolean = () =>
            true
    ): boolean {
        return !!config[inputName]?.includes(type) && additionalCondition(inputName, type, config);
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-object-type
    static isRequiredByType(types: {}, inputName: string, type: string): boolean {
        return !!types[inputName]?.find((a) => a === type);
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-object-type
    static textByType(types: {}, inputName: string, type: string): string {
        let foundType = types[inputName]?.find((a) => a.types.includes(type));
        if (!foundType) {
            foundType = types[inputName]?.find((a) => a.types.includes('default'));
        }
        return foundType ? foundType.name : '';
    }

    static containTwoWords(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            const inputValue = control?.value ?? '';
            const nonEmptyWords = inputValue.split(' ').filter((word) => word.trim().length > 0);
            const hasAtLeastTwoWords = nonEmptyWords.length >= 2;
            return hasAtLeastTwoWords ? null : { twoWords: true };
        };
    }

    static areAllEntriesValid(
        entries: any[],
        isEntryFilled: (entries: any[]) => boolean,
        minLength: number = 0
    ): boolean {
        let result = true;

        if (minLength && entries?.length < minLength) {
            result = false;
            return result;
        }

        const incomeFilledResults = [];

        for (const entry of entries) {
            const entryFilled = isEntryFilled(entry);
            incomeFilledResults.push(entryFilled);
            result = result && entryFilled;
            if (!result) {
                break;
            }
        }

        return result;
    }

    public static getAllErrors(form: UntypedFormGroup | UntypedFormArray): { [key: string]: any } {
        const errors: { [key: string]: any } = {};

        Object.keys(form.controls).forEach((key) => {
            const control = form.get(key);

            if (control instanceof UntypedFormGroup || control instanceof UntypedFormArray) {
                const childErrors = FormUtils.getAllErrors(control);
                if (Object.keys(childErrors).length > 0) {
                    errors[key] = childErrors;
                }
            } else if (control && control.errors) {
                errors[key] = control.errors;
            }
        });

        return errors;
    }
}
